/**
 * ARA Home OS — Concept F · The Residence Journal
 * Master specimen page. Not a production surface.
 *
 * Route: /home-os/design
 *
 * Signs of life:
 *   · Ambient light phase (dawn / morning / afternoon / evening / night)
 *   · Dynamic greeting personalised to the resident
 *   · Household pulse (live status with breathing dots)
 *   · Editorial entrance rise (staggered 6px fade)
 *   · Softly personal AI-feeling briefing copy
 */

import { useMemo, useState } from "react";
import {
  ResidenceLayout, Masthead, RoomNav, useLightPhase, HouseholdPulse,
  MorningBrief, EditorialSection, ModuleMark, Tag,
  Rule, Caption, Standfirst, Figure, FigureRow,
  TextLink, PillButton, ListItem, Colophon,
  MODULE_TONE,
} from "./components/os/editorial";

const HOUSEHOLD = "Ngakuru Home";
const RESIDENT = "Erana";

/* Conventional labels — the "room" concept survives in the design
   language and hint text, not in the primary label. */
const ROOMS = [
  { key: 'today',     label: 'Today',      hint: 'the foyer',           tone: 'today'     },
  { key: 'family',    label: 'Family',     hint: 'people & plan',       tone: 'family'    },
  { key: 'meals',     label: 'Meals',      hint: 'the kitchen',         tone: 'meals'     },
  { key: 'shopping',  label: 'Shopping',   hint: 'the pantry',          tone: 'pantry'    },
  { key: 'money',     label: 'Money',      hint: 'the study',           tone: 'money'     },
  { key: 'health',    label: 'Health',     hint: 'the wellness room',   tone: 'wellness'  },
  { key: 'documents', label: 'Documents',  hint: 'the library',         tone: 'documents' },
  { key: 'home',      label: 'Home',       hint: 'maintenance',         tone: 'home'      },
  { key: 'goals',     label: 'Goals',      hint: 'planning studio',     tone: 'goals'     },
];

/* Personalised briefing library — chosen by the current phase.
   Feels written specifically for the Ngakuru family. */
function useBriefing({ phase, greeting, closingHint }) {
  return useMemo(() => {
    const family = {
      resident: 'Erana',
      partner:  'Marama',
      children: ['Kaia', 'Ivy'],
    };
    switch (phase) {
      case 'dawn':
        return {
          headline: "The house is stirring. Two soft things ask before the day begins.",
          standfirst: `Kaia’s dentist appointment is at ten. The kitchen is running low on oats and milk — nothing urgent, but worth ordering with breakfast. Marama is already in the kitchen.`,
          closing: closingHint,
          items: [
            { time: '10:00', module: 'health',   moduleLabel: 'Health',   title: 'Kaia · dentist, Ponsonby',       meta: 'twenty-five minutes · leave the house by 09:30.' },
            {                module: 'meals',    moduleLabel: 'Meals',    title: 'Provisions running low',         meta: 'olive oil, oats, milk · order with breakfast.' },
          ],
        };
      case 'morning':
        return {
          headline: `Good morning, ${family.resident}. Three matters ask for you today.`,
          standfirst: `Kaia has a dentist appointment before lunch, the rent is scheduled for Tuesday, and the kitchen is quietly running low. Ivy is settled at school, and the house is otherwise in hand.`,
          closing: closingHint,
          items: [
            { time: '10:00', module: 'health',   moduleLabel: 'Health',   title: 'Kaia · dentist, Ponsonby',       meta: 'twenty-five minutes · leave the house by 09:30.' },
            { time: 'Tue',   module: 'money',    moduleLabel: 'Money',    title: 'Rent · $412 · Property Trust',   meta: 'auto-scheduled from the household float.' },
            {                module: 'meals',    moduleLabel: 'Meals',    title: 'Kitchen provisions running low', meta: 'olive oil, oats, milk · order suggested for Wednesday.' },
          ],
        };
      case 'afternoon':
        return {
          headline: `Good afternoon, ${family.resident}. Two things remain on the day.`,
          standfirst: `Kaia is back from the dentist and settled again. The insurance renewal notice arrived this morning and is waiting in Documents — nothing urgent, but worth reading before the weekend.`,
          closing: closingHint,
          items: [
            { time: '15:30', module: 'family',    moduleLabel: 'Family',    title: 'School pickup · Ivy',           meta: 'ten minutes · Marama is on her way.' },
            {                module: 'documents', moduleLabel: 'Documents', title: 'Insurance renewal · State',     meta: 'in the library · renews on the 28th.' },
          ],
        };
      case 'evening':
        return {
          headline: `Good evening, ${family.resident}. The house is settling in.`,
          standfirst: `Dinner is in hand — the miso salmon planned yesterday. Kaia and Ivy are home. One small thing remains: a note from the school about Wednesday's trip is waiting in Documents.`,
          closing: closingHint,
          items: [
            { time: '18:30', module: 'meals',     moduleLabel: 'Meals',     title: 'Miso salmon · four settings',   meta: 'rice was set on Sunday · twenty minutes.' },
            {                module: 'documents', moduleLabel: 'Documents', title: 'School note · Wednesday trip',  meta: 'requires a signed reply by Monday.' },
          ],
        };
      case 'night':
      default:
        return {
          headline: `The house is quiet, ${family.resident}. Only what asks for tomorrow.`,
          standfirst: `Tomorrow’s brief is already written. The rent will move on Tuesday, Kaia has a play date after school, and Ivy has swimming. Nothing else has been added to the day.`,
          closing: closingHint,
          items: [
            { time: 'Tue',   module: 'money',   moduleLabel: 'Money',   title: 'Rent · $412 · Property Trust',   meta: 'auto-scheduled overnight.' },
            { time: '15:30', module: 'family',  moduleLabel: 'Family',  title: 'Kaia · play date after school',  meta: 'with the Whitfield children.' },
            { time: '16:00', module: 'health',  moduleLabel: 'Health',  title: 'Ivy · swimming, Herne Bay',       meta: 'Marama is driving.' },
          ],
        };
    }
  }, [phase, greeting, closingHint]);
}

const TODAY_LABEL = new Date().toLocaleDateString('en-NZ', {
  weekday: 'long', day: 'numeric', month: 'long',
});

const ROOM_PALETTE = [
  { key: 'today',     label: 'Today',      tone: 'today',     hex: '#2C2C2A', note: 'Charcoal · the arrival'          },
  { key: 'family',    label: 'Family',     tone: 'family',    hex: '#7A6E63', note: 'Warm taupe · people'             },
  { key: 'meals',     label: 'Meals',      tone: 'meals',     hex: '#7C8B6E', note: 'Soft sage · nourishment'         },
  { key: 'shopping',  label: 'Shopping',   tone: 'pantry',    hex: '#6E6F55', note: 'Olive · provisions'              },
  { key: 'money',     label: 'Money',      tone: 'money',     hex: '#24382C', note: 'Deep forest · finances'          },
  { key: 'health',    label: 'Health',     tone: 'wellness',  hex: '#4E6E5D', note: 'Eucalyptus · well-being'         },
  { key: 'documents', label: 'Documents',  tone: 'documents', hex: '#4A4E55', note: 'Slate · records'                 },
  { key: 'home',      label: 'Home',       tone: 'home',      hex: '#5C4033', note: 'Walnut · maintenance'            },
  { key: 'goals',     label: 'Goals',      tone: 'goals',     hex: '#B59A6D', note: 'Brushed brass · plans'           },
];

export default function ResidenceJournalSpecimenPage() {
  const [currentRoom, setCurrentRoom] = useState('today');
  const [manualPhase, setManualPhase] = useState(null); // for testing phases

  const auto = useLightPhase(RESIDENT);
  const active = manualPhase
    ? {
        phase: manualPhase,
        greeting: {
          dawn: `The house is stirring, ${RESIDENT}`,
          morning: `Good morning, ${RESIDENT}`,
          afternoon: `Good afternoon, ${RESIDENT}`,
          evening: `Good evening, ${RESIDENT}`,
          night: `The house is quiet, ${RESIDENT}`,
        }[manualPhase],
        closingHint: {
          dawn: 'The day is only just beginning.',
          morning: 'The house is otherwise calm.',
          afternoon: 'Two things remain on the day.',
          evening: 'The house is settling in.',
          night: 'Sleep is the day\u2019s last chore.',
        }[manualPhase],
        hourLabel: auto.hourLabel,
      }
    : auto;

  const brief = useBriefing(active);

  const rail = (
    <div className="flex flex-col h-full">
      <Masthead
        residence={HOUSEHOLD}
        dateLabel={TODAY_LABEL}
      />
      <div className="mt-12">
        <RoomNav
          rooms={ROOMS}
          currentKey={currentRoom}
          onNavigate={setCurrentRoom}
        />
      </div>
      <div className="mt-12">
        <p className="caption-ara" style={{ letterSpacing: '0.16em' }}>Concierge</p>
        <p className="mt-2 prose-ara text-[13px]">Anything the house can help with?</p>
        <div className="mt-4">
          <TextLink testid="rail-ask-ara">Ask ARA</TextLink>
        </div>
      </div>
      <div className="mt-auto pt-12">
        <p className="caption-ara" style={{ letterSpacing: '0.16em' }}>Light of day</p>
        <div className="mt-3 flex flex-wrap gap-x-3 gap-y-2">
          {['auto', 'dawn', 'morning', 'afternoon', 'evening', 'night'].map((p) => {
            const isActive = (p === 'auto' && !manualPhase) || p === manualPhase;
            return (
              <button
                key={p}
                type="button"
                onClick={() => setManualPhase(p === 'auto' ? null : p)}
                data-testid={`phase-${p}`}
                className="link-ara"
                style={{
                  fontSize: '12px',
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: isActive ? 'var(--ara-ink)' : 'var(--ara-ink-soft)',
                  backgroundSize: isActive ? '100% 1px' : '0% 1px',
                }}
              >
                {p}
              </button>
            );
          })}
        </div>
        <p className="mt-3 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
          Currently · {active.phase} · {active.hourLabel}
        </p>
      </div>
    </div>
  );

  return (
    <ResidenceLayout rail={rail} phase={active.phase} testid="specimen-page">
      {/* ── The Morning Brief ─────────────────────────────────────── */}
      <MorningBrief
        dated={`${TODAY_LABEL} · Auckland · 17° · ${active.phase}`}
        resident={`${active.greeting}.`}
        pulse={
          <HouseholdPulse
            items={[
              { label: 'Marama · home',         tone: 'family',   live: true  },
              { label: 'Kaia · at school',      tone: 'health',   live: false },
              { label: 'Ivy · at school',       tone: 'health',   live: false },
              { label: 'Kitchen · quiet',       tone: 'meals',    live: false },
              { label: '21° inside',            tone: 'home',     live: true  },
            ]}
          />
        }
        headline={brief.headline}
        standfirst={brief.standfirst}
        items={brief.items}
        closing={brief.closing}
      />

      {/* ── The Rooms specimen ────────────────────────────────────── */}
      <EditorialSection
        testid="specimen-rooms"
        moduleMark={{ module: 'neutral', label: 'The Residence' }}
        title="The rooms of the residence"
        subtitle="Familiar labels for wayfinding. The room metaphor lives on quietly in the hint text — and structurally, in the way each module composes."
      >
        <div>
          {ROOM_PALETTE.map((r, i) => (
            <div
              key={r.key}
              className={`grid grid-cols-[24px_1fr_auto] items-baseline gap-6 py-5 ${i === 0 ? 'border-t' : ''} border-b`}
              style={{ borderColor: 'var(--ara-line-soft)' }}
              data-testid={`room-swatch-${r.key}`}
            >
              <span className="dot-ara" style={{ background: MODULE_TONE[r.tone] }} aria-hidden />
              <div className="min-w-0">
                <p className="font-display text-[22px] leading-tight" style={{ fontWeight: 400, color: 'var(--ara-ink)' }}>
                  {r.label}
                </p>
                <p className="mt-1 prose-ara text-[13.5px]">{r.note}</p>
              </div>
              <p className="font-mono text-[11.5px] tabular-nums" style={{ color: 'var(--ara-ink-soft)', letterSpacing: '0.08em' }}>
                {r.hex}
              </p>
            </div>
          ))}
        </div>
      </EditorialSection>

      {/* ── Living signs specimen ─────────────────────────────────── */}
      <EditorialSection
        testid="specimen-life"
        moduleMark={{ module: 'family', label: 'Signs of life' }}
        title="The canvas is sacred"
        subtitle="The walls stay still. The light within them changes throughout the day."
      >
        <div className="space-y-8">
          <div>
            <Caption>Live status · breathing dots</Caption>
            <div className="mt-4">
              <HouseholdPulse
                items={[
                  { label: 'Marama · home',      tone: 'family',   live: true  },
                  { label: 'Kaia · at school',   tone: 'health',   live: false },
                  { label: 'Kitchen · quiet',    tone: 'meals',    live: false },
                  { label: '21° inside',         tone: 'home',     live: true  },
                ]}
              />
            </div>
          </div>
          <div>
            <Caption>What actually shifts through the day</Caption>
            <p className="mt-3 prose-ara max-w-[54ch]">
              The warm ivory never changes. Only three things move, imperceptibly. The
              charcoal ink softens a fraction at dawn and again at night. The brushed
              brass accent — reserved for Goals — warms in the evening. And live
              status dots breathe on a slow three-second cycle. Nothing else.
            </p>
            <p className="mt-4 caption-ara">Tap a phase in the rail to preview.</p>
          </div>
          <div>
            <Caption>Colour is architecture, not decoration</Caption>
            <p className="mt-3 prose-ara max-w-[54ch]">
              A soft stone hairline separates rail from room. Each room carries one
              muted signature — sage, forest, eucalyptus, slate, walnut, brass — that
              appears only as a small dot, a section signature, or a hairline. Never
              as a fill. Never as a page.
            </p>
          </div>
          <div>
            <Caption>Copy is written for the family</Caption>
            <p className="mt-3 prose-ara max-w-[54ch]">
              The briefing names Marama, Kaia and Ivy. It reports what the house
              knows — never sells, never celebrates, never reminds twice. A generative
              model may phrase the words; the tone is fixed.
            </p>
          </div>
        </div>
      </EditorialSection>

      {/* ── Typography specimen ───────────────────────────────────── */}
      <EditorialSection
        testid="specimen-type"
        moduleMark={{ module: 'neutral', label: 'Typography' }}
        title="The residence reads in two voices"
        subtitle="Fraunces speaks in headlines and figures. Inter speaks in the body. Nothing else is heard."
      >
        <div className="space-y-10">
          <div>
            <Caption>Display · Fraunces 400 · 72</Caption>
            <p className="display-ara mt-3 text-[64px] leading-none">The morning</p>
          </div>
          <div>
            <Caption>Standfirst · Fraunces 400 · 26</Caption>
            <Standfirst className="mt-3">
              A quiet paragraph opens each room, drawn in Fraunces, set at a reading measure.
            </Standfirst>
          </div>
          <div>
            <Caption>Body · Inter 400 · 16 / 28</Caption>
            <p className="prose-ara mt-3 max-w-[60ch]">
              Running text is set in Inter at sixteen on twenty-eight. It carries meta, hints,
              descriptions, and everything the standfirst chooses not to say. It is never bold,
              never italic, never coloured, except in service of meaning.
            </p>
          </div>
          <div>
            <Caption>Caption · Inter 500 · 12 · tracked 0.14em</Caption>
            <p className="caption-ara mt-3">Sunday · 16 February · Auckland · morning</p>
          </div>
          <div>
            <Caption>Figure · Fraunces 400 · tabular</Caption>
            <p className="display-ara mt-3 text-[56px] leading-none tabular-nums tracking-[-0.02em]">$4,120.86</p>
          </div>
        </div>
      </EditorialSection>

      {/* ── Figures specimen ──────────────────────────────────────── */}
      <EditorialSection
        testid="specimen-figures"
        moduleMark={{ module: 'money', label: 'Figures' }}
        title="How numbers appear"
        subtitle="Fraunces, tabular. A small dot marks the room they belong to. No tile, no chart, no glow."
      >
        <FigureRow items={[
          { label: 'Household float', value: '$4,120', module: 'money',  hint: 'as of yesterday · covers 6 weeks' },
          { label: 'Groceries · week', value: '$186',  module: 'meals',  hint: '11% under this month' },
          { label: 'Sleep · Kaia',    value: '9h 12m', module: 'health', hint: 'seven-day rolling median' },
        ]}/>
      </EditorialSection>

      {/* ── Lists specimen ────────────────────────────────────────── */}
      <EditorialSection
        testid="specimen-list"
        moduleMark={{ module: 'meals', label: 'Meals · This Week' }}
        title="A list is a page, not a table"
        subtitle="Rows are separated by whitespace and a single hairline. No stripes, no icons, no chevrons."
      >
        <div>
          <ListItem module="meals" title="Monday · slow-roasted lamb"  meta="four people · from the freezer" value="90 min" />
          <ListItem module="meals" title="Tuesday · miso salmon bowls" meta="pantry ready · rice on Sunday" value="25 min" />
          <ListItem module="meals" title="Wednesday · pasta al limone" meta="Kaia’s request · uses last of the parmesan" value="20 min" />
          <ListItem module="meals" title="Thursday · roasted vegetables & haloumi" meta="clears the crisper" value="35 min" />
          <ListItem module="meals" title="Friday · take-away night"    meta="budgeted from the household float" value="—" last />
        </div>
      </EditorialSection>

      {/* ── Actions specimen ─────────────────────────────────────── */}
      <EditorialSection
        testid="specimen-actions"
        moduleMark={{ module: 'neutral', label: 'Actions' }}
        title="Actions almost disappear"
        subtitle="A text link is the default. A pill is reserved. Nothing fills, nothing shouts."
      >
        <div className="space-y-8">
          <div>
            <Caption>Primary link</Caption>
            <div className="mt-4"><TextLink testid="spec-link-primary">Move rent to Tuesday</TextLink></div>
          </div>
          <div>
            <Caption>Secondary link</Caption>
            <div className="mt-4"><TextLink testid="spec-link-secondary">Open the money room</TextLink></div>
          </div>
          <div>
            <Caption>Occasional pill (outline)</Caption>
            <div className="mt-4"><PillButton testid="spec-pill-outline">Confirm</PillButton></div>
          </div>
          <div>
            <Caption>Rarest pill (solid)</Caption>
            <div className="mt-4"><PillButton variant="solid" testid="spec-pill-solid">Approve payment</PillButton></div>
          </div>
          <div>
            <Caption>Ghost, whispered</Caption>
            <div className="mt-4"><PillButton variant="ghost" testid="spec-pill-ghost">Later</PillButton></div>
          </div>
          <div>
            <Caption>Inline tags</Caption>
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-3">
              <Tag module="health">Health</Tag>
              <Tag module="meals">Meals</Tag>
              <Tag module="money">Money</Tag>
              <Tag module="documents">Documents</Tag>
              <Tag module="goals">Goals</Tag>
            </div>
          </div>
        </div>
      </EditorialSection>

      {/* ── Empty state ──────────────────────────────────────────── */}
      <EditorialSection
        testid="specimen-empty"
        moduleMark={{ module: 'documents', label: 'An empty room' }}
        title="When a room has nothing to report"
        subtitle="The residence never apologises. It simply says what is."
      >
        <div className="py-12">
          <p className="font-display text-[32px] leading-snug max-w-[24ch]" style={{ fontWeight: 400, color: 'var(--ara-ink)' }}>
            The library is quiet.
          </p>
          <p className="mt-6 prose-ara max-w-[52ch]">
            No documents have arrived this week. When one does, it will be waiting here.
          </p>
          <div className="mt-8">
            <TextLink testid="spec-empty-link">Bring a document in</TextLink>
          </div>
        </div>
      </EditorialSection>

      {/* ── Motion note ──────────────────────────────────────────── */}
      <EditorialSection
        testid="specimen-motion"
        moduleMark={{ module: 'neutral', label: 'Motion' }}
        title="Movement is composed"
        subtitle="Content rises six pixels into place at 640ms, staggered. The ink softens across the day at 1.4 seconds. Live dots breathe on a three-second cycle. Numbers do not animate."
      >
        <div>
          <div className="border-t border-b py-6" style={{ borderColor: 'var(--ara-line-soft)' }}>
            <p className="caption-ara">Content entrance</p>
            <p className="mt-3 font-display text-[22px]" style={{ fontWeight: 400 }}>
              640ms · rise 6px · staggered 80ms
            </p>
          </div>
          <div className="border-b py-6" style={{ borderColor: 'var(--ara-line-soft)' }}>
            <p className="caption-ara">Ink through the day</p>
            <p className="mt-3 font-display text-[22px]" style={{ fontWeight: 400 }}>
              1,400ms · charcoal softens fractionally
            </p>
          </div>
          <div className="border-b py-6" style={{ borderColor: 'var(--ara-line-soft)' }}>
            <p className="caption-ara">Living pulse</p>
            <p className="mt-3 font-display text-[22px]" style={{ fontWeight: 400 }}>
              3.6s · gentle breath on live-status dots
            </p>
          </div>
          <div className="border-b py-6" style={{ borderColor: 'var(--ara-line-soft)' }}>
            <p className="caption-ara">Reduced motion</p>
            <p className="mt-3 font-display text-[22px]" style={{ fontWeight: 400 }}>
              1ms · anyone who prefers stillness
            </p>
          </div>
        </div>
      </EditorialSection>

      <Colophon testid="specimen-colophon">
        ARA · Ngakuru Home · Set in Fraunces &amp; Inter · Printed on warm ivory · Composed at {active.hourLabel}
      </Colophon>
    </ResidenceLayout>
  );
}
